import React from 'react';
import Table from '@cloudscape-design/components/table';
import Header from '@cloudscape-design/components/header';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import TextFilter from '@cloudscape-design/components/text-filter';
import Pagination from '@cloudscape-design/components/pagination';
import { useCollection } from '@cloudscape-design/collection-hooks';

import { ResultRow } from '../api/ForecastApi';
import { toCsv } from '../utils/csv';
import { resolveColumn, capToTopGroups, MAX_GROUPS } from '../utils/groups';

const PAGE_SIZE = 25;

// Identifier columns are numeric but must NOT be rendered with thousands
// separators (e.g. fgid 100005 must stay "100005", not "100,005"). They are
// codes, not quantities.
const ID_COLUMNS = new Set(['fg', 'fgid', 'batch_id', 'run_id', 'query_id']);

// Round numeric cell values to at most 2 decimals for display (CSV export keeps full precision).
// `columnId` lets us skip locale grouping for identifier columns.
function fmtCell(v: unknown, columnId?: string): string {
  if (v == null) return '';
  const n = typeof v === 'number' ? v : Number(v);
  const isNumeric = typeof v === 'number' || (v !== '' && Number.isFinite(n));
  if (!isNumeric) return String(v);
  // Identifiers: render the raw integer with no grouping separators.
  if (columnId && ID_COLUMNS.has(columnId)) return String(n);
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function downloadCsv(rows: ResultRow[], fileName: string): void {
  const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

interface ResultsTableProps {
  rows: ResultRow[];
  fileName?: string;
  groupColumn?: string;
  groupMatch?: (key: string) => boolean;
}

export default function ResultsTable({
  rows,
  fileName = 'forecast-results.csv',
  groupColumn,
  groupMatch,
}: ResultsTableProps): JSX.Element {
  const columnIds = React.useMemo(() => {
    const keys = new Set<string>();
    rows.forEach((row) => Object.keys(row).forEach((k) => keys.add(k)));
    const ids = Array.from(keys);
    if (ids.includes('fgid') && ids.includes('site_id')) {
      const anchor = Math.min(ids.indexOf('fgid'), ids.indexOf('site_id'));
      const ordered = ids.filter((k) => k !== 'fgid' && k !== 'site_id');
      ordered.splice(anchor, 0, 'fgid', 'site_id');
      return ordered;
    }
    return ids;
  }, [rows]);

  // Display is capped to the top groups; CSV export uses all rows.
  const { rows: displayRows, total, capped } = React.useMemo(() => {
    const resolved = groupColumn
      ? resolveColumn(columnIds, groupColumn, groupMatch)
      : undefined;
    return capToTopGroups(rows, resolved);
  }, [rows, columnIds, groupColumn, groupMatch]);

  const columnDefinitions = React.useMemo(
    () =>
      columnIds.map((id) => ({
        id,
        header: id,
        cell: (item: ResultRow) => fmtCell(item[id], id),
        sortingField: id,
        minWidth: 150,
      })),
    [columnIds]
  );

  const { items, collectionProps, filterProps, paginationProps, filteredItemsCount } =
    useCollection(displayRows, {
      filtering: {
        empty: <Box textAlign="center">No results</Box>,
        noMatch: <Box textAlign="center">No matches</Box>,
      },
      pagination: { pageSize: PAGE_SIZE },
      sorting: {},
    });

  return (
    <Table
      {...collectionProps}
      items={items}
      columnDefinitions={columnDefinitions}
      variant="embedded"
      stickyHeader
      resizableColumns
      empty={<Box textAlign="center">No data</Box>}
      header={
        <Header
          counter={capped ? `(top ${MAX_GROUPS} of ${total})` : `(${rows.length})`}
          actions={
            <Button
              iconName="download"
              onClick={() => downloadCsv(rows, fileName)}
              disabled={rows.length === 0}
            >
              Download CSV
            </Button>
          }
        >
          Results
        </Header>
      }
      filter={
        <TextFilter
          {...filterProps}
          filteringPlaceholder="Filter results"
          countText={`${filteredItemsCount ?? 0} matches`}
        />
      }
      pagination={<Pagination {...paginationProps} />}
    />
  );
}
